// @oasis-permission-inherits: /jerboa-codec-report (jerboa:view)
import { Link, createFileRoute } from "@tanstack/react-router";

import { Badge } from "~/components/ui/badge";
import { Skeleton } from "~/components/ui/skeleton";
import { formatTimestamp } from "~/modules/jerboa-client-preview/formatters";
import { getJerboaCodecReportQuery } from "~/modules/jerboa-codec-report/queries";
import type { JerboaCodecReportDetail } from "~/modules/jerboa-codec-report/schemas";

export const Route = createFileRoute("/_dash/jerboa-codec-report/$id")({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const { data, isLoading, error } = getJerboaCodecReportQuery.useQuery({
    pathParams: { id },
  });
  const report = data?.result;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/jerboa-codec-report" className="text-sm text-blue-600 hover:underline">
            {"<"} Codec Reports
          </Link>
          <h1 className="mt-2 text-xl font-bold">Codec Report</h1>
        </div>
        {report && (
          <Badge variant={report.status === "failure" ? "destructive" : "outline"}>
            {report.status}
          </Badge>
        )}
      </div>

      {isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full rounded-md" />
          <Skeleton className="h-48 w-full rounded-md" />
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          <p className="font-medium">Failed to load codec report</p>
          <pre className="mt-2 whitespace-pre-wrap text-xs">{error.message}</pre>
        </div>
      )}

      {report && (
        <>
          <div className="grid gap-3 md:grid-cols-3">
            <InfoCard title="Codec" rows={codecRows(report)} />
            <InfoCard title="Browser" rows={browserRows(report)} />
            <InfoCard title="Project" rows={projectRows(report)} />
          </div>

          <section className="rounded-md border p-4">
            <h2 className="mb-2 text-sm font-semibold">Message</h2>
            <p className="whitespace-pre-wrap text-sm">{report.message || "-"}</p>
          </section>

          <section className="rounded-md border p-4">
            <h2 className="mb-2 text-sm font-semibold">Probe Results</h2>
            <pre className="overflow-auto rounded bg-muted p-3 text-xs">
              {JSON.stringify(report.probeResults ?? [], null, 2)}
            </pre>
          </section>

          <section className="rounded-md border p-4">
            <h2 className="mb-2 text-sm font-semibold">Raw Payload</h2>
            <pre className="overflow-auto rounded bg-muted p-3 text-xs">
              {JSON.stringify(report, null, 2)}
            </pre>
          </section>
        </>
      )}
    </div>
  );
}

type InfoRow = [string, string | null | undefined];

function InfoCard({ title, rows }: { title: string; rows: InfoRow[] }) {
  return (
    <section className="rounded-md border p-4">
      <h2 className="mb-3 text-sm font-semibold">{title}</h2>
      <dl className="space-y-2 text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="grid grid-cols-[110px_1fr] gap-3">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="min-w-0 truncate font-mono text-xs">{value || "-"}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function codecRows(report: JerboaCodecReportDetail): InfoRow[] {
  return [
    ["String", report.codec.string],
    ["Family", report.codec.family],
    ["Profile", report.codec.profile],
    ["Pixel", report.codec.pixelFormat],
    ["Chroma", report.codec.chromaSubsampling],
    ["Bit Depth", report.codec.bitDepth?.toString()],
    ["Size", `${report.codec.width ?? "-"}×${report.codec.height ?? "-"}`],
    ["Acceleration", report.hardwareAcceleration],
    [
      "Decode",
      typeof report.decodeDurationMs === "number"
        ? `${Math.round(report.decodeDurationMs)}ms`
        : null,
    ],
  ];
}

function browserRows(report: JerboaCodecReportDetail): InfoRow[] {
  return [
    ["Browser", report.browserInfo?.browserName],
    ["OS", report.browserInfo?.os],
    ["OS Version", report.browserInfo?.osVersion],
    ["GPU", report.browserInfo?.gpuRenderer],
    ["Version", report.appVersion ?? report.jerboaVersion],
    ["Created", formatTimestamp(report.createdAt)],
  ];
}

function projectRows(report: JerboaCodecReportDetail): InfoRow[] {
  return [
    ["Status", report.status],
    ["Source", report.source],
    ["Asset", report.assetId],
    ["Video", report.videoId],
    ["Project", report.projectId],
    ["Captions", report.captionsProjectId],
    ["Session", report.sessionId],
  ];
}
